import React from 'react';

import isFunction from 'lodash/isFunction';
import { UploadedFile } from 'types';

type RenderProps = {
    fileInputRef: React.RefObject<any>;
    isSubmitting: boolean;
    handleFileChange: (ev) => void;
    handleUploadClear: (ev) => void;
    uploadProgressValue: null | number;
    getCalculatedProgress: () => number;
    uploadFile: (file) => void;
};

type Props = {
    apiRequest: ({ file, onProgress, cancelController }) => Promise<UploadedFile>;
    children: (renderProps: RenderProps) => React.ReactNode;
    onUploadStart?: (file) => void;
    onUploadSuccess?: (file: UploadedFile) => void;
    onUploadError?: (err) => void;
    onUploadClear?: (ev?) => void;
    onChange?: (ev) => void;
};

const defaultProps = {
    onUploadStart: () => {},
    onUploadSuccess: () => {},
    onUploadError: () => {},
    onUploadClear: () => {},
    onChange: () => {},
};

type State = {
    files: null | string[];
    isSubmitting: boolean;
    uploadProgressValue: number | null;
    uploadProcessingValue: number | null;
    errorMessage: null;
};

class FileUploader extends React.PureComponent<Props, State> {
    static defaultProps = defaultProps;

    state = {
        files: null,
        isSubmitting: false,
        uploadProgressValue: null,
        uploadProcessingValue: null,
        errorMessage: null,
    };

    mounted = false;
    cancelController = {
        cancel: (message?: string) => {},
    };
    fileInputRef = React.createRef();

    componentDidMount() {
        this.mounted = true;
    }

    componentWillUnmount() {
        this.cancelController.cancel();
        this.mounted = false;
    }

    getFile = () => {
        const { files = [] } = this.state;
        return files[0];
    };

    handleUpload = () => {
        const { isSubmitting } = this.state;
        const file = this.getFile();

        if (isSubmitting || !file) {
            return;
        }

        this.setState(
            {
                isSubmitting: true,
                uploadProgressValue: 0,
                uploadProcessingValue: 0,
                errorMessage: null,
            },
            () => {
                this.props.onUploadStart(file);
                this.props
                    .apiRequest({
                        file,
                        onProgress: this.handleUploadProgress,
                        cancelController: this.cancelController,
                    })
                    .then(this.handleUploadSuccess)
                    .catch(this.handleUploadError);
            }
        );
    };

    handleUploadClear = (ev?) => {
        if (ev) {
            ev.preventDefault();
        }

        this.setState(
            {
                isSubmitting: false,
                uploadProgressValue: null,
                uploadProcessingValue: null,
                errorMessage: null,
            },
            () => {
                this.cancelController.cancel();
                this.props.onUploadClear();
            }
        );
    };

    handleUploadProgress = (progress) => {
        if (!this.mounted || !progress.lengthComputable) {
            return;
        }

        const { total, loaded } = progress;

        this.setState({
            uploadProgressValue: loaded / total,
        });
    };

    handleUploadSuccess = (json) => {
        const streamResponse = json.reverse()[0];
        if ('error' in streamResponse) {
            // handle errors in stream response
            this.handleUploadError(streamResponse['error']);
        } else if ('data' in streamResponse) {
            this.setState(
                {
                    isSubmitting: false,
                    uploadProgressValue: 1,
                    uploadProcessingValue: 1,
                    errorMessage: null,
                },
                () => {
                    this.props.onUploadSuccess(streamResponse['data']);
                }
            );
        } else {
            throw Error('Invalid response');
        }
    };

    handleUploadError = (err) => {
        if (!this.mounted) {
            return;
        }

        this.setState(
            {
                isSubmitting: false,
                files: null,
                uploadProgressValue: null,
                uploadProcessingValue: null,
                errorMessage: err.message,
            },
            () => {
                if (this.fileInputRef && this.fileInputRef.current) {
                    this.fileInputRef.current.value = '';
                }

                if (err.constructor.name !== 'Cancel') {
                    this.props.onUploadError(err);
                }
            }
        );
    };

    handleFileChange = (ev) => {
        this.props.onChange(ev);

        const { files } = ev.target;

        this.setState({ files }, () =>
            files.length === 0 ? this.handleUploadClear() : this.handleUpload()
        );
    };

    getCalculatedProgress = () => {
        const progressValue = (this.state.uploadProgressValue / 4) * 3;
        const processingValue = this.state.uploadProcessingValue / 4;

        return (progressValue + processingValue) * 100;
    };

    uploadFile = (file) => {
        if (file == null) {
            return;
        }

        this.setState({ files: [file] }, () => this.handleUpload());
    };

    render() {
        const { children } = this.props;

        if (!children || !isFunction(children)) {
            console.error('FileUploader: Please provide a render-prop callback as a child');
            return null;
        }

        const renderProps: RenderProps = {
            uploadFile: this.uploadFile,
            fileInputRef: this.fileInputRef,
            isSubmitting: this.state.isSubmitting,
            handleFileChange: this.handleFileChange,
            handleUploadClear: this.handleUploadClear,
            uploadProgressValue: this.state.uploadProgressValue,
            getCalculatedProgress: this.getCalculatedProgress,
        };

        return children(renderProps);
    }
}

export default FileUploader;
